import React, { useCallback, useEffect, useReducer } from 'react';
import { Grid } from '@nextiva/next-ui-components';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useQueries } from 'react-query';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';

import SelectiveDrawerForm from 'components/SelectiveDrawerForm';
import Drawer from 'components/Drawer';
import { Skeleton } from 'components/Skeleton';
import { CallForwardSelective } from 'components/CallForwardSelective';
import { CallForwardUnanswered } from 'components/CallForwardUnanswered';
import { CallForwardBusy } from 'components/CallForwardBusy';
import { CallForwardAlways } from 'components/CallForwardAlways';
import { SvgOpenClose } from 'components/SvgOpenClose';
import { SectionSaveCancel } from 'components/SectionSaveCancel';
import { useSnackbar } from 'hooks';
import { HuntGroupService } from 'services';
import { ContainerStyles } from 'styles/application/phone-number';
import { formSchema } from 'utils/phone-number';
import {
  CallForwardContext,
  initialState,
  callForwardingReducer,
  actionTypes,
} from './context';

export const CallForwarding = ({ huntGroupId = '' }) => {
  const [state, dispatch] = useReducer(callForwardingReducer, initialState);
  const setSnackbar = useSnackbar();
  const contextValue = { state, dispatch };

  const allSectionsOpen = Object.values(state.openSections).every(
    (value) => value === true,
  );

  const methods = useForm({
    shouldUnregister: false,
    defaultValues: {
      always: {
        enabled: false, // used to control the state of the switch
        phoneNumber: '',
      },
      selective: {
        availableSchedules: null,
        conditionList: [],
        enabled: false,
        phoneNumber: '',
        playRingBack: false,
      },
      unanswered: {
        enabled: false,
        forwardToVoicemail: false,
        forwardToPhoneNumber: '',
        forwardTimeoutSeconds: 0,
      },
      busy: {
        enabled: false,
        phoneNumber: '',
      },
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: yupResolver(formSchema(dispatch)),
  });
  const { control, handleSubmit, reset, setValue } = methods;

  const resGet = useQueries([
    {
      queryKey: ['callForwardAlways', huntGroupId],
      queryFn: () => HuntGroupService.getCallForwardAlways(huntGroupId),
    },
    {
      queryKey: ['callForwardBusy', huntGroupId],
      queryFn: () => HuntGroupService.getCallForwardBusy(huntGroupId),
    },
    {
      queryKey: ['callForwardUnanswered', huntGroupId],
      queryFn: () => HuntGroupService.getCallForwardUnanswered(huntGroupId),
    },
    {
      queryKey: ['callForwardSelective', huntGroupId],
      queryFn: () => HuntGroupService.getCallForwardSelective(huntGroupId),
    },
  ]);

  const fieldArrayMethods = useFieldArray({
    control,
    name: 'selective.conditionList',
  });

  const openAllSections = () => {
    dispatch({ type: actionTypes.ALL_SECTIONS_TOGGLED, payload: true });
  };

  const closeAllSections = () => {
    dispatch({ type: actionTypes.ALL_SECTIONS_TOGGLED, payload: false });
  };

  const renderFormFields = useCallback(
    (state) => {
      reset({
        always: {
          enabled: state.originalEnabled.always || false,
          phoneNumber: state.originalFields.always.phoneNumber || '',
        },
        selective: {
          availableSchedules:
            state.originalFields.selective.availableSchedules || [],
          conditionList: state.originalFields.selective.conditionList || [],
          enabled: state.originalEnabled.selective || false,
          phoneNumber: state.originalFields.selective.phoneNumber || '',
          playRingBack: state.originalFields.selective.playRingBack || false,
        },
        busy: {
          enabled: state.originalEnabled.busy || false,
          phoneNumber: state.originalFields.busy.phoneNumber || '',
        },
        unanswered: {
          enabled: !!state.originalFields.unanswered.forwardToPhoneNumber,
          forwardToVoicemail: state.originalFields.unanswered.forwardToVoicemail
            ? 'true'
            : 'false',
          forwardToPhoneNumber:
            state.originalFields.unanswered.forwardToPhoneNumber || '',
          forwardTimeoutSeconds:
            state.originalFields.unanswered.forwardTimeoutSeconds || 0,
        },
      });

      // this is required to register the condition list
      if (Object.keys(state.originalFields.selective).length > 0) {
        setValue(
          'selective.conditionList',
          state.originalFields.selective?.conditionList?.map((c) => ({ ...c })),
        );
      }

      dispatch({ type: actionTypes.RESET_DIRTY });
    },
    [reset, setValue],
  );

  const informUser = (responseArr) => {
    let errors = 0;
    let message = 'There was an error while saving call forwarding';
    let severity = 'error';
    const numOfSections = Object.keys(state.originalEnabled).length;

    responseArr.forEach((result, index) => {
      if (result.status === 'rejected') {
        if (errors > 0) message += ',';
        if (errors > numOfSections - 1) message += ', and'; // Oxford Comma :)

        // This logic MUST be tied to the indices of the promises in the `Promise.allSettled` from `saveChanges`
        if (index === 0) message += ' always';
        if (index === 1) message += ' busy';
        if (index === 2) message += ' unanswered';
        if (index === 3) message += ' selective';

        errors += 1;
      }
    });
    message +=
      ' information for this team. Please try again later or contact support if the issue persists.';

    if (errors === 0) {
      message = 'Changes saved successfully.';
      severity = 'success';
    }

    setSnackbar({
      message,
      severity,
    });
  };

  const saveChanges = async (formData) => {
    const { always, busy, unanswered, selective } = formData;
    // If any field was updated or was previously enabled it will maintain that state, otherwise it will update with the following criteria:
    // If required fields are present, enable the feature automatically. If required fields are missing, disable the feature automatically.
    always.enabled =
      Boolean(always.phoneNumber) &&
      (state.dirtySections.always || state.originalEnabled.always);
    busy.enabled =
      Boolean(busy.phoneNumber) &&
      (state.dirtySections.busy || state.originalEnabled.busy);
    // convert radio button string value to a boolean before sending the DTO
    unanswered.forwardToVoicemail = unanswered.forwardToVoicemail === 'true';
    unanswered.forwardAfterTimeout = Boolean(unanswered.forwardToPhoneNumber);
    // unanswered enabled is not used in the puts dto, but is used for tracking its state in the component
    delete unanswered.enabled;
    if (unanswered.forwardToVoicemail) delete unanswered.forwardToPhoneNumber;
    if (
      selective.phoneNumber &&
      selective.conditionList.length > 0 &&
      (state.dirtySections.selective || state.originalEnabled.selective)
    ) {
      selective.enabled = true;
    }

    // if there isn't at least one active condition disable selective
    if (
      selective.phoneNumber &&
      selective.conditionList.every((c) => c.active === false)
    ) {
      selective.enabled = false;
    }

    try {
      const res = await Promise.allSettled([
        HuntGroupService.updateCallForwardAlways(huntGroupId, always),
        HuntGroupService.updateCallForwardBusy(huntGroupId, busy),
        HuntGroupService.updateCallForwardUnanswered(huntGroupId, unanswered),
        HuntGroupService.updateCallForwardSelective(huntGroupId, selective),
      ]);

      // the put is dropping the available schedules form the response object
      // The following is a patch until the response object is fixed on the backend
      if (res[3].status === 'fulfilled') {
        res[3].value.availableSchedules = selective.availableSchedules;
      }

      const newAlways =
        res[0].status === 'fulfilled'
          ? res[0].value
          : {
              phoneNumber: state.originalFields.always.phoneNumber,
              enabled: state.originalEnabled.always,
            };

      const newBusy =
        res[1].status === 'fulfilled'
          ? res[1].value
          : {
              phoneNumber: state.originalFields.busy.phoneNumber,
              enabled: state.originalEnabled.busy,
            };

      const newUnanswered =
        res[2].status === 'fulfilled'
          ? res[2].value
          : {
              forwardToVoicemail: state.originalFields.unanswered
                .forwardToVoicemail
                ? 'true'
                : 'false',
              forwardToPhoneNumber:
                state.originalFields.unanswered.forwardToPhoneNumber,
              forwardTimeoutSeconds:
                state.originalEnabled.unanswered.forwardTimeoutSeconds,
            };

      const newSelective =
        res[3].status === 'fulfilled'
          ? res[3].value
          : {
              availableSchedules:
                state.originalFields.selective.availableSchedules,
              conditionList: state.originalFields.selective.conditionList,
              enabled: state.originalFields.selective.enabled,
              phoneNumber: state.originalFields.selective.phoneNumber,
              playRingBack: state.originalFields.selective.playRingBack,
            };

      informUser(res);
      dispatch({
        type: actionTypes.CHANGES_SAVED,
        payload: {
          always: newAlways,
          busy: newBusy,
          selective: newSelective,
          unanswered: newUnanswered,
        },
      });
    } catch (error) {
      setSnackbar({
        message:
          'There was an error while saving call forwarding information for this team. Please try again later or contact support if the issue persists.',
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    if (
      resGet[0].isLoading ||
      resGet[1].isLoading ||
      resGet[2].isLoading ||
      resGet[3].isLoading ||
      !huntGroupId
    ) {
      return;
    }
    const originalFields = {
      always: resGet[0].data,
      busy: resGet[1].data,
      unanswered: resGet[2].data,
      selective: resGet[3].data,
    };

    dispatch({
      type: actionTypes.ORIGINAL_DATA_LOADED,
      payload: originalFields,
    });

    if (
      resGet[0].isError ||
      resGet[1].isError ||
      resGet[2].isError ||
      resGet[3].isError
    ) {
      setSnackbar({
        message:
          'There was an error while retrieving call forwarding information for this team. Please try again later or contact support if the issue persists.',
        severity: 'error',
      });
    }
  }, [
    resGet[0].isLoading,
    resGet[1].isLoading,
    resGet[2].isLoading,
    resGet[3].isLoading,
  ]);

  useEffect(() => {
    renderFormFields(state);
  }, [renderFormFields, state.originalFields, state.originalEnabled]);

  useEffect(
    () => () => {
      if (state.updated) {
        resGet[0].remove();
        resGet[1].remove();
        resGet[2].remove();
        resGet[3].remove();
      }
    },
    [state.updated],
  );

  useEffect(() => {
    if (!state.snackbar.type || !state.snackbar.message) return;

    setSnackbar({
      message: state.snackbar.message,
      severity: state.snackbar.type,
    });
  }, [state.snackbar]);

  if (state.loading) {
    // TODO: the Skeleton for this component and TeamPhoneNumber should more closely resemble the actual UI
    return <Skeleton />;
  }

  return (
    <CallForwardContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <ContainerStyles style={{ width: 620 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} className="header-padding">
              <div
                className="m-settings-header"
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwarding-Header"
              >
                <h1
                  className="m-settings-header__heading"
                  data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwarding-HeaderText"
                >
                  Forwarding
                </h1>
                <button
                  className="header-close-button"
                  onClick={allSectionsOpen ? closeAllSections : openAllSections}
                  type="button"
                  data-testid={`PlatformAdminUI-TeamVoiceSettings-CallForwarding-HeadercloseButton-${
                    allSectionsOpen ? 'Close' : 'Open'
                  }`}
                >
                  <span>{allSectionsOpen ? 'Close' : 'Open'} all sections</span>
                  <SvgOpenClose toggle={allSectionsOpen} />
                </button>
              </div>
            </Grid>
            <form
              id="call-forwarding"
              onSubmit={handleSubmit(saveChanges)}
              data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwarding-Form"
            >
              <CallForwardAlways huntGroupId={huntGroupId} />
              <CallForwardSelective
                fieldArrayMethods={fieldArrayMethods}
                huntGroupId={huntGroupId}
              />
              <CallForwardUnanswered huntGroupId={huntGroupId} />
              <CallForwardBusy huntGroupId={huntGroupId} />
              <SectionSaveCancel
                cancelOnClick={() => renderFormFields(state)}
                open={
                  state.dirtySections.always ||
                  state.dirtySections.busy ||
                  state.dirtySections.unanswered ||
                  state.dirtySections.selective
                }
                saveOnClick={handleSubmit(saveChanges)}
              />
            </form>
          </Grid>
          <Drawer
            fields={fieldArrayMethods.fields}
            title="Forwarding condition"
          >
            <ContainerStyles>
              <SelectiveDrawerForm
                appendCondition={fieldArrayMethods.append}
                conditionList={fieldArrayMethods.fields}
                updateCondition={fieldArrayMethods.update}
              />
            </ContainerStyles>
          </Drawer>
        </ContainerStyles>
      </FormProvider>
    </CallForwardContext.Provider>
  );
};

CallForwarding.propTypes = {
  huntGroupId: PropTypes.string.isRequired,
};
